import { config } from '@/lib/config';
import { LEGACY_SUBSCRIPTIONS } from '@/lib/mqtt/subscriptions';
import { useDataStreamStore } from '@/lib/store/dataStream';
import { useDronesStore } from '@/lib/store/drones';
import { useDeferredValue, useMemo } from 'react';
import { useShallow } from 'zustand/shallow';
import type { TopicNode } from '../types';
import { type TopicStatInput, buildTopicTree } from '../utils/buildTopicTree';
import { topicMatchesFilter } from '../utils/filterDefs';

/** A topic counts as "live" if a message landed within this window. */
const LIVE_WINDOW_MS = 3_000;

/** Filter a built tree to nodes whose leaves match the search string. The tree
 *  search is navigation — it matches topic *names*. Searching inside messages
 *  is the message pane's job, scoped to the selected topic. */
function pruneBySearch(nodes: TopicNode[], q: string): TopicNode[] {
  if (!q) return nodes;
  const lower = q.toLowerCase();
  const walk = (node: TopicNode): TopicNode | null => {
    if (node.leaf) return node.path.toLowerCase().includes(lower) ? node : null;
    const kids = node.children.map(walk).filter((n): n is TopicNode => n !== null);
    return kids.length ? { ...node, children: kids } : null;
  };
  return nodes.map(walk).filter((n): n is TopicNode => n !== null);
}

export function useTopicTree() {
  const topics = useDataStreamStore((s) => s.topics);
  const filter = useDataStreamStore((s) => s.filter);
  // Typing must not stall the live feed's renders.
  const search = useDeferredValue(useDataStreamStore((s) => s.search)).trim();
  // Online drones expand each per-drone '+' subscription into a concrete
  // placeholder (…/aircraft/{serial}/drones/events) so the tree shows we're
  // listening on *this* drone's channel, not just the abstract '+' wildcard.
  const droneIds = useDronesStore(useShallow((s) => Object.keys(s.byId)));

  return useMemo(() => {
    const now = Date.now();
    const stats: TopicStatInput[] = [...topics.values()]
      .filter((t) => topicMatchesFilter(t.topic, filter))
      .map((t) => ({ topic: t.topic, count: t.count, lastSeen: t.lastSeen }));
    const liveCount = stats.length;

    // Seed every subscribed-but-silent topic as a count-0 placeholder so the
    // tree shows what the app subscribes to, not just what the broker has
    // produced. Live mode only (mock uses a different namespace). Concrete
    // subs that already have traffic are skipped (the real stat wins); wildcard
    // subs (networkid/#, …) are seeded as their literal filter so the '#'/'+'
    // segment shows as a dimmed marker and real children nest beside it. For
    // per-drone '+' subs: expand into one concrete placeholder per online drone
    // (so an online SIMTL80 reads as "listening on its events channel, none
    // yet"), and drop the bare '+' node in that case — it would be a redundant
    // duplicate branch. The literal '+' is only seeded when no drone is online,
    // as a "subscribed, waiting for any drone" marker.
    if (!config.isMock) {
      const seen = new Set(stats.map((s) => s.topic));
      const seed = (topic: string) => {
        if (seen.has(topic)) return;
        if (!topicMatchesFilter(topic, filter)) return;
        stats.push({ topic, count: 0, lastSeen: 0 });
        seen.add(topic);
      };
      for (const sub of LEGACY_SUBSCRIPTIONS) {
        if (sub.includes('/aircraft/+/') && droneIds.length) {
          for (const id of droneIds) seed(sub.replace('/aircraft/+/', `/aircraft/${id}/`));
        } else {
          seed(sub);
        }
      }
    }

    const tree = buildTopicTree(stats, LIVE_WINDOW_MS, now);
    return {
      tree: pruneBySearch(tree, search),
      leafCount: stats.length,
      liveCount,
      searching: search.length > 0,
    };
  }, [topics, filter, search, droneIds]);
}
